import { useEffect, useRef, useState } from "react";
import { FeedbackApi } from "../api";
import { FeedbackData, FEEDBACK_STATUS, FeedbackStatus } from "../../../types";

const STATUS_ORDER = Object.values(FEEDBACK_STATUS);
// Actioned items are the ones waiting on Sam's approval, so that section leads
// the list and stays visible even when empty. Approved sits right below it.
// infra-hold leads everything (only shown when non-empty): merging is blocked on Sam.
const LEADING_STATUSES: FeedbackStatus[] = [
  FEEDBACK_STATUS.INFRA_HOLD,
  FEEDBACK_STATUS.ACTIONED,
  FEEDBACK_STATUS.APPROVED,
];
const DISPLAY_ORDER: FeedbackStatus[] = [
  ...LEADING_STATUSES,
  ...STATUS_ORDER.filter((status) => !LEADING_STATUSES.includes(status)),
];
// Merged (on dev) and released (Sam has deployed it to prod; the user sees
// "Ready to try") are finished work, so their sections start collapsed.
const DONE_STATUSES: FeedbackStatus[] = [FEEDBACK_STATUS.MERGED, FEEDBACK_STATUS.RELEASED];

const COPIED_MS = 1500;
// Only used when the id text could not be selected: otherwise the failure hint
// stays until the selection goes away (see the selectionchange effect below).
const COPY_FAILED_MS = 5000;

// True while the selection is non-collapsed and still touches `el`.
export const selectionHoldsEl = (
  selection: Pick<Selection, "isCollapsed" | "containsNode"> | null,
  el: Node | null,
): boolean => !!selection && !!el && selection.isCollapsed === false && selection.containsNode(el, true);

// null covers both "hasn't loaded yet" and "not an admin" — either way the
// panel stays invisible rather than flashing an error at ordinary users.
export const AdminFeedbackPanel = () => {
  const [feedback, setFeedback] = useState<FeedbackData[] | null>(null);
  const [open, setOpen] = useState(false);
  // Merged and released items are done-and-dusted, so start their sections
  // collapsed — everything else starts open.
  const [openSections, setOpenSections] = useState<Record<FeedbackStatus, boolean>>(() =>
    STATUS_ORDER.reduce(
      (acc, status) => ({ ...acc, [status]: !DONE_STATUSES.includes(status) }),
      {} as Record<FeedbackStatus, boolean>,
    ),
  );

  const [refreshing, setRefreshing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyFailedId, setCopyFailedId] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The id element whose text was selected after a failed copy.
  const copyFailedEl = useRef<HTMLElement | null>(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // Drop the element once the hint is gone (dismissed, timed out or copy succeeded)
  // so a stale node never leaks into the selectionchange handler.
  useEffect(() => {
    if (!copyFailedId) copyFailedEl.current = null;
  }, [copyFailedId]);

  // The failure hint stays while the id is selected so the admin has as long as
  // they need to press Ctrl/Cmd+C; clicking elsewhere collapses the selection,
  // and selecting other text moves it off the id element.
  useEffect(() => {
    if (!copyFailedId) return;
    const onSelectionChange = () => {
      if (!selectionHoldsEl(window.getSelection(), copyFailedEl.current)) setCopyFailedId(null);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [copyFailedId]);

  const flashCopy = (id: string, ok: boolean, selected = false) => {
    setCopiedId(ok ? id : null);
    setCopyFailedId(ok ? null : id);
    clearTimeout(copiedTimer.current);
    if (!ok && selected) return;
    copiedTimer.current = setTimeout(() => {
      setCopiedId(null);
      setCopyFailedId(null);
    }, ok ? COPIED_MS : COPY_FAILED_MS);
  };

  // Clipboard unavailable or rejected (non-HTTPS, permission denied): select the
  // id text so the admin can finish with Ctrl/Cmd+C.
  const selectText = (el: HTMLElement): boolean => {
    const selection = window.getSelection();
    if (!selection) return false;
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
    copyFailedEl.current = el;
    return true;
  };

  const copyId = (id: string, el: HTMLElement) => {
    const fail = () => {
      flashCopy(id, false, selectText(el));
    };
    if (!navigator.clipboard) {
      fail();
      return;
    }
    navigator.clipboard
      .writeText(id)
      .then(() => flashCopy(id, true))
      .catch(fail);
  };

  const load = async () => {
    setRefreshing(true);
    const result = await FeedbackApi.getLog();
    if (result.success && result.data) {
      setFeedback(result.data.feedback);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (item: FeedbackData, status: FeedbackStatus) => {
    setFeedback(
      (f) => f?.map((existing) => (existing.id === item.id ? { ...existing, status } : existing)) ?? null,
    );
    await FeedbackApi.update({
      id: item.id,
      createdAt: item.createdAt,
      userId: item.userId,
      status,
    });
  };

  // Sam's go-ahead on an infra-held branch. infraAck is only accepted from an
  // admin login, and only counts if the branch tip hasn't moved since the hold.
  const ackInfra = async (item: FeedbackData) => {
    const result = await FeedbackApi.update({
      id: item.id,
      createdAt: item.createdAt,
      userId: item.userId,
      status: FEEDBACK_STATUS.APPROVED,
      infraAck: true,
      expectedStatus: FEEDBACK_STATUS.INFRA_HOLD,
    });
    if (result.success) {
      setFeedback(
        (f) =>
          f?.map((existing) =>
            existing.id === item.id
              ? { ...existing, status: FEEDBACK_STATUS.APPROVED, infraAck: true }
              : existing,
          ) ?? null,
      );
    } else {
      load();
    }
  };

  // Send an item back to the triage agent with a correction: status goes back
  // to "new" (so the agent's next run picks it up) and the note rides along.
  const sendBack = async (item: FeedbackData) => {
    const adminNote = drafts[item.id]?.trim();
    if (!adminNote) return;
    const result = await FeedbackApi.update({
      id: item.id,
      createdAt: item.createdAt,
      userId: item.userId,
      status: FEEDBACK_STATUS.NEW,
      adminNote,
      expectedStatus: item.status,
    });
    if (result.success) {
      setFeedback(
        (f) =>
          f?.map((existing) =>
            existing.id === item.id ? { ...existing, status: FEEDBACK_STATUS.NEW, adminNote } : existing,
          ) ?? null,
      );
      setDrafts((d) => ({ ...d, [item.id]: "" }));
    } else {
      // e.g. 409: the item changed under us — reload rather than guess.
      load();
    }
  };

  if (feedback === null) return null;

  return (
    <section className="pt-4 border-t">
      <div className="flex flex-wrap items-center gap-x-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="min-h-10 sm:min-h-0 text-left text-sm font-semibold text-neutral-500"
        >
          Feedback log ({feedback.filter((f) => f.status === FEEDBACK_STATUS.ACTIONED).length} actioned)
          {feedback.some((f) => f.status === FEEDBACK_STATUS.INFRA_HOLD) && (
            <span className="ml-2 text-[10px] bg-red-600 text-white rounded-full px-2 py-0.5">
              {feedback.filter((f) => f.status === FEEDBACK_STATUS.INFRA_HOLD).length} infra hold
            </span>
          )}
        </button>
        <button
          onClick={load}
          disabled={refreshing}
          className="min-h-10 sm:min-h-0 text-xs text-neutral-400 disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-3">
          {DISPLAY_ORDER.map((status) => {
            const items = feedback.filter((item) => item.status === status);
            if (items.length === 0 && status !== FEEDBACK_STATUS.ACTIONED) return null;
            // (infra-hold is also hidden when empty, via the line above)
            const sectionOpen = openSections[status];
            return (
              <div key={status}>
                <button
                  onClick={() =>
                    setOpenSections((sections) => ({ ...sections, [status]: !sections[status] }))
                  }
                  className="min-h-10 sm:min-h-0 text-xs font-semibold text-neutral-500 capitalize"
                >
                  {sectionOpen ? "▾" : "▸"} {status} ({items.length})
                </button>
                {sectionOpen && (
                  <ul className="space-y-1 mt-1">
                    {items.map((item) => (
                      <li
                        key={item.id}
                        className={`border rounded px-3 py-2 text-sm space-y-1 ${
                          item.status === FEEDBACK_STATUS.INFRA_HOLD
                            ? "bg-red-50 border-red-400 border-2"
                            : "bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="min-w-0 break-words">{item.text}</span>
                          <span className="text-xs text-neutral-400 capitalize whitespace-nowrap">
                            {item.status}
                          </span>
                        </div>
                        <p className="text-[10px] text-neutral-400 font-mono break-all">
                          <button
                            type="button"
                            onClick={(e) => copyId(item.id, e.currentTarget)}
                            title="Click to copy id"
                            className="min-h-10 sm:min-h-0 text-left select-all hover:text-neutral-600"
                          >
                            {item.id}
                          </button>
                          <span aria-live="polite" className={copiedId === item.id ? "ml-2" : undefined}>
                            {copiedId === item.id && "Copied"}
                          </span>
                          <span
                            aria-live="polite"
                            className={copyFailedId === item.id ? "ml-2 text-red-500" : undefined}
                          >
                            {copyFailedId === item.id && "Copy failed — press Ctrl/Cmd+C"}
                          </span>
                        </p>
                        {item.page && <p className="text-xs text-neutral-400 break-all">{item.page}</p>}
                        {item.context && (
                          <p className="text-xs text-neutral-400" title={item.context.userAgent}>
                            {item.context.platform} · {item.context.deviceType}
                            {item.context.screen &&
                              ` · screen ${item.context.screen.width}×${item.context.screen.height}`}
                            {item.context.viewport &&
                              ` · viewport ${item.context.viewport.width}×${item.context.viewport.height}`}
                          </p>
                        )}
                        {(item.origin || item.releaseId) && (
                          <p className="text-xs text-neutral-400">
                            {item.origin && (
                              <span className="bg-neutral-100 rounded px-1.5 py-0.5 mr-2">{item.origin}</span>
                            )}
                            {item.releaseId && <span className="font-mono">release: {item.releaseId}</span>}
                          </p>
                        )}
                        {item.status === FEEDBACK_STATUS.INFRA_HOLD && (
                          <div className="text-xs text-red-900 space-y-1">
                            <p className="font-semibold">
                              Infra change — merging is blocked until you acknowledge it.
                            </p>
                            {item.infraFiles && item.infraFiles.length > 0 && (
                              <ul className="font-mono list-disc ml-4 break-all">
                                {item.infraFiles.map((file) => (
                                  <li key={file}>{file}</li>
                                ))}
                              </ul>
                            )}
                            <button
                              onClick={() => ackInfra(item)}
                              className="min-h-10 sm:min-h-0 bg-red-700 text-white rounded px-2 py-1"
                            >
                              Infra OK — approve &amp; merge
                            </button>
                          </div>
                        )}
                        {item.resolutionNote && (
                          <p className="text-xs text-neutral-500 italic whitespace-pre-line break-words">{item.resolutionNote}</p>
                        )}
                        {item.adminNote && (
                          <p className="text-xs text-amber-700">Admin note: {item.adminNote}</p>
                        )}
                        {item.branch && (
                          <p className="text-xs text-neutral-400 font-mono break-all">branch: {item.branch}</p>
                        )}
                        {item.mergedAt && (
                          <p className="text-xs text-neutral-400">
                            merged {new Date(item.mergedAt).toLocaleString()}
                            {item.mergeCommit && (
                              <span className="font-mono"> · {item.mergeCommit.slice(0, 8)}</span>
                            )}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-x-3 text-xs">
                          {Object.values(FEEDBACK_STATUS).map((s) => (
                            <button
                              key={s}
                              onClick={() => setStatus(item, s)}
                              className={`min-h-10 sm:min-h-0 capitalize ${
                                item.status === s ? "font-semibold" : "text-neutral-400"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                        {item.status !== FEEDBACK_STATUS.NEW && (
                          <div className="flex gap-2 items-start">
                            <textarea
                              value={drafts[item.id] ?? ""}
                              onChange={(e) => setDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
                              placeholder="Clarify, or say what the agent got wrong…"
                              maxLength={2000}
                              rows={2}
                              className="min-w-0 flex-1 border rounded px-2 py-1 text-xs"
                            />
                            <button
                              onClick={() => sendBack(item)}
                              disabled={!drafts[item.id]?.trim()}
                              className="min-h-10 sm:min-h-0 text-xs text-neutral-500 disabled:opacity-40"
                            >
                              Send back to agent
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
